import _defaultTo from 'lodash/defaultTo';
import _head from 'lodash/head';
import _isEmpty from 'lodash/isEmpty';
import _isFinite from 'lodash/isFinite';
import _isNil from 'lodash/isNil';
import _map from 'lodash/map';
import _orderBy from 'lodash/orderBy';
import _reduce from 'lodash/reduce';
import _reject from 'lodash/reject';
import _toNumber from 'lodash/toNumber';
// Serialization
import { deserialize, serialize } from 'serializr';
import { rolloutDeviceSchema, rolloutSchema } from './schemas';
// Color
import { rolloutStatusPalette } from '../components/core/colors';
// Localization
import { standardDateOptions } from '../intl/LocalizedComponents';
// Models
/* eslint-disable import/named */
import { title as distributionTitle } from './distribution';
/* eslint-enable import/named */
// Custom
import { SEARCHFIELD_TYPES } from '../constants/App';
import { findInList } from '../utils/Tools';

/**
* Serialization
*/
const serializerKey = 'rollout';

const deserializeRollout = (data) => deserialize(rolloutSchema, data);

const serializeRollout = ({
  date, forceCompleteUpdate, distributionId, deviceIds,
}) => ({
  [serializerKey]: {
    ...serialize(rolloutSchema, {
      date: _defaultTo(date, null),
      ...(forceCompleteUpdate && { force_complete_update: forceCompleteUpdate }),
      distribution_id: _defaultTo(distributionId, null),
      ...(deviceIds && { device_ids: deviceIds }),
    }),
  },
});

const deserializeRolloutDevice = (data) => deserialize(rolloutDeviceSchema, data);

/**
* Lists
*/
const rolloutStatusesList = [
  {
    key: 'pending',
    value: 'pending',
    labelId: 'rollout.status.pending',
    icon: 'pending',
    color: rolloutStatusPalette.pending,
  },
  {
    key: 'in_progress',
    value: 'in_progress',
    labelId: 'rollout.status.in_progress',
    icon: 'progressUp',
    color: rolloutStatusPalette.inProgress,
  },
  {
    key: 'cancelled',
    value: 'cancelled',
    labelId: 'rollout.status.cancelled',
    icon: 'cancelled',
    color: rolloutStatusPalette.cancelled,
  },
  {
    key: 'stopped',
    value: 'stopped',
    labelId: 'rollout.status.stopped',
    icon: 'stopped',
    color: rolloutStatusPalette.stopped,
  },
  {
    key: 'success',
    value: 'success',
    labelId: 'rollout.status.success',
    icon: 'success',
    color: rolloutStatusPalette.success,
  },
  {
    key: 'error',
    value: 'error',
    labelId: 'rollout.status.error',
    icon: 'error',
    color: rolloutStatusPalette.error,
  },
];

/**
* Utils
*/
const rolloutTitle = (rolloutData, intl) => {
  if (rolloutData) {
    const { date, distribution } = rolloutData;
    return `${intl.formatDate(date, standardDateOptions)} (${distributionTitle(distribution)})`;
  }
  return '';
};

const rolloutStatuses = (rollout, dir = 'desc') => (
  _orderBy(_map(rollout.statuses, (value, key) => ({
    date: key,
    value,
  })), ['date'], [dir])
);

const rolloutStatus = (status) => findInList(rolloutStatusesList, 'value', status, 'value');

const rolloutLastStatus = (rollout) => {
  const statusesArray = rolloutStatuses(rollout);

  if (!_isEmpty(statusesArray)) {
    // Return an object with date an full last status attributes
    const lastStatus = _head(statusesArray);
    return {
      date: lastStatus.date,
      status: rolloutStatus(lastStatus),
    };
  }
  return null;
};

const rolloutDevicesQtities = (rollout) => (
  _reduce(rolloutStatusesList, (result, status) => {
    const devicesStatusQtity = _toNumber(rollout.devices_quantities[status.value]);
    if (_isFinite(devicesStatusQtity) && devicesStatusQtity > 0) {
      result.push({
        status,
        qtity: devicesStatusQtity,
      });
    }
    return result;
  }, [])
);

const rolloutDeleteOperation = (rollout) => {
  const lastStatus = rolloutLastStatus(rollout);
  if (lastStatus) {
    const { status: { value } } = lastStatus;
    if (value === 'pending') {
      return 'cancelRollout';
    }
    if (value === 'in_progress') {
      return 'stopRollout';
    }
  }
  return null;
};

/*
Transforms:
{
  2019-01-17 09:08:57.241252Z: {status: "pending"},
  2019-01-17 09:09:34.241252Z: {status: "success", desc: "desc"}
}
Into:
[
  {date: "2019-01-17 09:08:57.241252Z", value: "pending"},
  {date: "2019-01-17 09:09:34.241252Z", value: "success", desc: "desc"},
]
*/
const deviceRolloutStatuses = (deviceRollout, dir = 'desc') => (
  _orderBy(_map(deviceRollout.statuses, ({ status, desc }, key) => ({
    date: key,
    value: status,
    desc,
  })), ['date'], [dir])
);

const deviceRolloutLastStatus = (deviceRollout) => {
  const statusesArray = deviceRolloutStatuses(deviceRollout);

  if (!_isEmpty(statusesArray)) {
    // Return last status
    return _head(statusesArray);
  }
  return null;
};

/**
* Model
*/
const rollout = {
  // Name and icon
  name: 'rollout',
  icon: 'rollout',
  deviceName: 'deviceRollout',

  // Serialization
  deserialize: deserializeRollout,
  serialize: serializeRollout,
  deserializeDevice: deserializeRolloutDevice,

  // Lists
  statusesList: rolloutStatusesList,
  devicesStatusesList: _reject(rolloutStatusesList, ['value', 'stopped']),

  // Utils
  title: rolloutTitle,
  statuses: rolloutStatuses,
  status: rolloutStatus,
  lastStatus: rolloutLastStatus,
  devicesQtities: rolloutDevicesQtities,
  deleteOperation: rolloutDeleteOperation,
  deviceStatuses: deviceRolloutStatuses,
  deviceLastStatus: deviceRolloutLastStatus,

  // List features
  listFeatures: {
    // Default list params
    defaultParams: {
      pagination: {
        page: 1,
        pageSize: 20,
      },
      sort: [
        { field: 'date', dir: 'desc' },
      ],
    },

    // Columns
    columnsQuantity: () => ({
      xs: 2,
      sm: 2,
      md: 4,
      lg: 5,
      xl: 5,
    }),
    columns: [
      {
        key: 'date',
        labelId: 'field.date',
        field: 'date',
        sortable: true,
      },
      {
        key: 'distribution',
        labelId: 'field.distribution',
        field: 'distribution',
        sortable: false,
        className: 'hidden-sm-down',
      },
      {
        key: 'completeUpdate',
        labelId: 'field.completeUpdate',
        field: 'completeUpdate',
        sortable: false,
        className: 'table-content-2 table-content-center hidden-md-down',
      },
      {
        key: 'status',
        labelId: 'field.status',
        field: 'status',
        sortable: false,
        className: 'table-content-1 table-content-center',
      },
      {
        key: 'devices',
        labelId: 'field.devices',
        field: 'devices',
        sortable: false,
        className: 'hidden-sm-down',
      },
    ],

    // Search fields
    searchFields: [
      {
        key: 'status',
        labelId: 'field.status',
        field: 'status',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.enum,
        filter: {
          field: 'last_status',
        },
      },
      {
        key: 'begin',
        labelId: 'field.afterDate',
        field: 'begin',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.date,
        scope: 'after',
        filter: {
          field: 'date',
        },
      },
      {
        key: 'end',
        labelId: 'field.beforeDate',
        field: 'end',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.date,
        scope: 'before',
        filter: {
          field: 'date',
        },
      },
      {
        key: 'distribution',
        labelId: 'field.distribution',
        field: 'distribution',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: true,
        excludeFilterFields: ['distribution', 'distribution_id'],
        type: SEARCHFIELD_TYPES.resource,
        filter: {
          field: 'distribution_id',
          key: 'id',
        },
      },
      {
        key: 'device',
        labelId: 'field.device',
        field: 'device',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: true,
        type: SEARCHFIELD_TYPES.resource,
        filter: {
          field: 'device_id',
          key: 'id',
        },
      },
    ],
  },

  // Devices list features
  devicesListFeatures: {
    // Default list params
    defaultParams: {
      pagination: {
        page: 1,
        pageSize: 20,
      },
      sort: [
        { field: 'last_status_date', dir: 'desc' },
      ],
    },

    // Columns
    columnsQuantity: () => ({
      xs: 3,
      sm: 3,
      md: 3,
      lg: 3,
      xl: 3,
    }),
    columns: [
      {
        key: 'last_status_date',
        labelId: 'field.dateLastStatus',
        field: 'last_status_date',
        sortable: true,
      },
      {
        key: 'device',
        labelId: 'field.device',
        field: 'device.serial',
        alias: 'device_serial',
        sortable: true,
      },
      {
        key: 'statuses',
        labelId: 'field.statusesHistory',
        field: 'statuses',
        sortable: false,
      },
    ],

    // Search fields
    searchFields: [
      {
        key: 'status',
        labelId: 'field.status',
        field: 'status',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.enum,
        filter: {
          field: 'last_status',
        },
      },
      {
        key: 'device',
        labelId: 'field.device',
        field: 'device',
        alias: 'device_id',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: true,
        type: SEARCHFIELD_TYPES.resource,
        filter: {
          field: 'device.id',
          key: 'id',
        },
      },
    ],
  },
};

module.exports = rollout;



// WEBPACK FOOTER //
// ./models/rollout.js